import {
  CreateJobRequest,
  CreateMaterialUploadRequest,
  Job,
  jobCancelPath,
  jobMaterialPath,
  JobMaterialResponse,
  jobMaterialResponseSchema,
  jobPath,
  JOBS_PATH,
  jobSchema,
  ListJobsResponse,
  listJobsResponseSchema,
  MATERIAL_UPLOAD_PATH,
  MaterialUploadResponse,
  materialUploadResponseSchema,
  SPEND_PATH,
  SpendResponse,
  spendResponseSchema,
} from '@quizdb/common';
import { quizdbApi } from '../api/quizdbApi';

/** The generation jobs' endpoints (QZ-D48, QZ-D55, QZ-D57). */
export const jobApi = quizdbApi.injectEndpoints({
  endpoints: (builder) => ({
    listJobs: builder.infiniteQuery<ListJobsResponse, void, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (last) => last.nextCursor,
      },
      query: ({ pageParam }) => ({
        url: JOBS_PATH,
        params: pageParam === undefined ? {} : { cursor: pageParam },
      }),
      transformResponse: (response: unknown) => listJobsResponseSchema.parse(response),
      providesTags: ['JobList'],
    }),
    getJob: builder.query<Job, string>({
      query: (jobId) => jobPath(jobId),
      transformResponse: (response: unknown) => jobSchema.parse(response),
      providesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }],
    }),
    createJob: builder.mutation<Job, CreateJobRequest>({
      query: (request) => ({ url: JOBS_PATH, method: 'POST', body: request }),
      transformResponse: (response: unknown) => jobSchema.parse(response),
      invalidatesTags: ['JobList', 'Spend'],
    }),
    cancelJob: builder.mutation<Job, string>({
      query: (jobId) => ({ url: jobCancelPath(jobId), method: 'POST' }),
      transformResponse: (response: unknown) => jobSchema.parse(response),
      invalidatesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }, 'JobList'],
    }),
    getSpend: builder.query<SpendResponse, void>({
      query: () => SPEND_PATH,
      transformResponse: (response: unknown) => spendResponseSchema.parse(response),
      providesTags: ['Spend'],
    }),
    // An upload is not a job until `POST /jobs`; createJob already invalidates JobList (PDG-D6).
    createMaterialUpload: builder.mutation<MaterialUploadResponse, CreateMaterialUploadRequest>({
      query: (body) => ({ url: MATERIAL_UPLOAD_PATH, method: 'POST', body }),
      transformResponse: (response: unknown) => materialUploadResponseSchema.parse(response),
    }),
    // Every click fetches a fresh 5-minute URL; nothing is cached (PDG-D11).
    getJobMaterial: builder.query<JobMaterialResponse, string>({
      query: (jobId) => jobMaterialPath(jobId),
      transformResponse: (response: unknown) => jobMaterialResponseSchema.parse(response),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useListJobsInfiniteQuery,
  useGetJobQuery,
  useCreateJobMutation,
  useCancelJobMutation,
  useGetSpendQuery,
  useCreateMaterialUploadMutation,
  useLazyGetJobMaterialQuery,
} = jobApi;
